const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '—'

const infoRow = (label, value) => ({
  columns: [
    { text: label, width: 130, fontSize: 8.5, color: '#777777' },
    { text: value || '—', width: '*', fontSize: 8.5, bold: true, color: '#1A1A1A' },
  ],
  margin: [0, 3, 0, 3],
})

const summaryRow = (label, value) => [
  { text: label, fontSize: 9, color: '#555555', margin: [8, 7, 4, 7] },
  { text: value, fontSize: 9, bold: true, color: '#1565C0', alignment: 'right', margin: [4, 7, 8, 7] },
]

export function buildReportPDF(student, attendanceList, orgLogoBase64 = null) {
  const org = student?.organizationId || {}

  const totalKm = attendanceList.reduce((sum, a) => sum + (Number(a.kmDriven) || 0), 0)
  const totalMins = attendanceList.reduce((sum, a) => sum + (Number(a.minutesDriven) || 0), 0)
  const totalSessions = attendanceList.length

  const sorted = [...attendanceList].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
  const completionDate = sorted[0]?.createdAt ? fmtDate(sorted[0].createdAt) : '—'

  const orgAddress = [org.address, org.city, org.state, org.pincode].filter(Boolean).join(', ')
  const studentAddress = [student.address, student.city, student.state, student.pincode].filter(Boolean).join(', ')
  const classTypes = [...new Set(attendanceList.map(a => a.classType).filter(c => c && c !== '-'))].join(', ')

  const tableBody = [
    [
      { text: 'Sr. No.', style: 'tHead', alignment: 'center' },
      { text: 'KM Driven', style: 'tHead', alignment: 'center' },
      { text: 'Minutes\nDriven', style: 'tHead', alignment: 'center' },
      { text: 'Instructor', style: 'tHead' },
      { text: 'Class Type', style: 'tHead' },
      { text: 'Date', style: 'tHead' },
    ],
    ...attendanceList.map((a, i) => {
      const bg = i % 2 === 0 ? '#FFFFFF' : '#F5F7FA'
      return [
        { text: String(i + 1), style: 'tCell', fillColor: bg, alignment: 'center' },
        { text: String(a.kmDriven || 0), style: 'tCell', fillColor: bg, alignment: 'center' },
        { text: String(a.minutesDriven || 0), style: 'tCell', fillColor: bg, alignment: 'center' },
        { text: a.driver || '—', style: 'tCell', fillColor: bg },
        { text: a.classType || '—', style: 'tCell', fillColor: bg },
        { text: a.date || fmtDate(a.createdAt), style: 'tCell', fillColor: bg },
      ]
    }),
  ]

  return {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [40, 40, 40, 60],
    info: { title: `Report-${student.name}` },
    defaultStyle: { font: 'Roboto' },

    footer: () => ({
      text: 'This is a System Generated Document and does not require Physical Signature. Powered By: DRIVOLO',
      fontSize: 7.5,
      color: '#AAAAAA',
      italics: true,
      alignment: 'center',
      margin: [40, 8, 40, 0],
    }),

    content: [
      // ── 1. SCHOOL HEADER ──────────────────────────────────────
      {
        table: {
          widths: ['*'],
          body: [[{
            stack: [
              {
                text: (org.certificateName || org.name || '').toUpperCase(),
                fontSize: 18,
                bold: true,
                color: '#FFFFFF',
                alignment: 'center',
                margin: [0, 12, 0, 3],
              },
              {
                text: orgAddress || '',
                fontSize: 8.5,
                color: '#C5CAE9',
                alignment: 'center',
                margin: [0, 0, 0, 12],
              },
            ],
            fillColor: '#1565C0',
          }]],
        },
        layout: 'noBorders',
        margin: [0, 0, 0, 8],
      },

      // ── 2. LICENSE | FORM TITLE | ROLL NO ─────────────────────
      {
        columns: [
          {
            width: '*',
            text: org.license ? `License No. : ${org.license}` : '',
            fontSize: 8.5,
            color: '#555555',
          },
          {
            width: 'auto',
            text: 'FORM - 14 & 15',
            fontSize: 10,
            bold: true,
            alignment: 'center',
            color: '#1565C0',
          },
          {
            width: '*',
            text: student?.displayId ? `Roll No. : ${student.displayId}` : '',
            fontSize: 8.5,
            color: '#555555',
            alignment: 'right',
          },
        ],
        margin: [0, 0, 0, 6],
      },
      {
        canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: '#1565C0' }],
        margin: [0, 0, 0, 14],
      },

      // ── 3. STUDENT INFO + PHOTO + TRAINING SUMMARY ────────────
      {
        columns: [
          {
            width: '58%',
            stack: [
              { text: 'STUDENT INFORMATION', style: 'sectionLabel', margin: [0, 0, 0, 6] },
              infoRow('Name', student?.name),
              infoRow("Father's / Husband's Name", student?.guardianName),
              infoRow('Date of Birth', student?.dob ? fmtDate(student.dob) : null),
              infoRow('Blood Group', student?.bloodGroup),
              infoRow('Phone Number', student?.phoneNumber),
              infoRow('Email', student?.email),
              infoRow('Address', studentAddress),
              infoRow('DL / Learning Licence No.', student?.learningLicenseNumber),
              infoRow('Class / Type of Vehicle Trained In', classTypes || '—'),
            ],
          },
          {
            width: '42%',
            stack: [
              { text: 'DRIVING TRAINING SUMMARY', style: 'sectionLabel', margin: [0, 0, 0, 6] },
              {
                table: {
                  widths: ['*', 'auto'],
                  body: [
                    summaryRow('Total KM Driven', `${totalKm} KM`),
                    summaryRow('Total Minutes Driven', `${totalMins} Min`),
                    summaryRow('Total Sessions', String(totalSessions)),
                    summaryRow('Completion Date', completionDate),
                  ],
                },
                layout: {
                  hLineWidth: () => 0.5,
                  vLineWidth: () => 0,
                  hLineColor: () => '#E8EAF6',
                  fillColor: (row) => (row % 2 === 0 ? '#F8F9FF' : '#FFFFFF'),
                  paddingLeft: () => 0,
                  paddingRight: () => 0,
                  paddingTop: () => 0,
                  paddingBottom: () => 0,
                },
              },
            ],
          },
        ],
        margin: [0, 0, 0, 14],
      },

      // ── 4. ADDRESS ─────────────────────────────────────────────
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: '#DDDDDD' }], margin: [0, 0, 0, 8] },
      { text: 'ADDRESS', style: 'sectionLabel', margin: [0, 0, 0, 4] },
      { text: studentAddress || '—', fontSize: 9, color: '#555555', margin: [0, 0, 0, 12] },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: '#DDDDDD' }], margin: [0, 0, 0, 14] },

      // ── 5. ATTENDANCE TABLE ────────────────────────────────────
      {
        table: {
          headerRows: 1,
          widths: [30, '*', '*', '*', '*', '*'],
          body: tableBody,
        },
        layout: {
          hLineWidth: (i, node) => (i === 0 || i === node.table.body.length) ? 0 : 0.5,
          vLineWidth: () => 0,
          hLineColor: () => '#E0E0E0',
          paddingLeft: () => 6,
          paddingRight: () => 6,
          paddingTop: () => 5,
          paddingBottom: () => 5,
        },
        margin: [0, 0, 0, 28],
      },

      // ── 6. CERTIFICATION TEXT ──────────────────────────────────
      {
        text: 'This is to certify that the above mentioned candidate has undergone the driving Training as per the guidelines\nPrescribed by the motor Vehicles Act, 1988 and the Central Motor Vehicle Rules, 1989.',
        fontSize: 9,
        color: '#444444',
        italics: true,
        alignment: 'center',
        margin: [0, 0, 0, 60],
      },

      // ── 7. SIGNATURES + LOGO ──────────────────────────────────
      {
        columns: [
          {
            width: '*',
            stack: [
              { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 130, y2: 0, lineWidth: 0.5, lineColor: '#AAAAAA' }], margin: [0, 0, 0, 4] },
              { text: 'STUDENT SIGNATURE', fontSize: 8, bold: true, color: '#555555' },
            ],
          },
          {
            width: 100,
            alignment: 'center',
            stack: orgLogoBase64
              ? [{ image: orgLogoBase64, width: 90, height: 90, alignment: 'center' }]
              : [{ text: (org.name || '').toUpperCase(), fontSize: 8, bold: true, color: '#1565C0', alignment: 'center' }],
          },
          {
            width: '*',
            alignment: 'right',
            stack: [
              { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 160, y2: 0, lineWidth: 0.5, lineColor: '#AAAAAA' }], margin: [0, 0, 0, 4] },
              { text: 'AUTHORIZED SIGNATORY', fontSize: 8, bold: true, color: '#555555', alignment: 'right' },
              { text: (org.name || '').toUpperCase(), fontSize: 7.5, color: '#1565C0', alignment: 'right', margin: [0, 2, 0, 0] },
            ],
          },
        ],
        columnGap: 10,
      },
    ],

    styles: {
      sectionLabel: { fontSize: 7.5, bold: true, color: '#1565C0', characterSpacing: 1.2 },
      tHead: { fontSize: 8, bold: true, color: '#FFFFFF', fillColor: '#1565C0' },
      tCell: { fontSize: 8, color: '#333333' },
    },
  }
}
